import { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { auth, db } from '../firebase';
import JobAidDrawer from './JobAidDrawer';

import Dashboard from '../pages/Dashboard';
import DriverVerification from '../pages/DriverVerification';
import HirerVerification from '../pages/HirerVerification';
import UserManagement from '../pages/UserManagement';
import Notifications from '../pages/Notifications';
import Subscriptions from '../pages/Subscriptions';
import Privacy from '../pages/Privacy';
import AdminManagement from '../pages/AdminManagement';
import JobManagement from '../pages/JobManagement';
import ReportsPage from '../pages/ReportsPage';
import InsuranceConfirmations from '../pages/InsuranceConfirmations';
import DeclarationsPage from '../pages/DeclarationsPage';
import OfferCodesPage from '../pages/OfferCodesPage';
import DisputesPage from '../pages/DisputesPage';

const NAV = [
  { id: 'dashboard',     icon: '📊', label: 'Dashboard' },
  { id: 'drivers',       icon: '🧑‍✈️', label: 'Driver Verification' },
  { id: 'hirers',        icon: '🏢', label: 'Hirer Verification' },
  { id: 'users',         icon: '👥', label: 'User Management' },
  { id: 'reports',       icon: '🚨', label: 'Reports' },
  { id: 'disputes',      icon: '⚖️', label: 'Disputes' },
  { id: 'notifications', icon: '🔔', label: 'Notifications' },
  { id: 'jobs',          icon: '💼', label: 'Job Management' },
  { id: 'insurance',     icon: '🛡️', label: 'Insurance Confirmations' },
  { id: 'declarations',  icon: '🪪', label: 'Declarations & Alerts' },
  { id: 'subscriptions', icon: '💳', label: 'Subscriptions' },
  { id: 'offercodes',    icon: '🏷️', label: 'Offer Codes' },
  { id: 'privacy',       icon: '🔒', label: 'Privacy & PIPEDA' },
  { id: 'admins',        icon: '🔐', label: 'Admin Management' },
];

export default function AdminLayout({ user }) {
  const [active, setActive]             = useState('dashboard');
  const [jobAidOpen, setJobAidOpen]     = useState(false);
  const [collapsed, setCollapsed]             = useState(false);
  const [theme, setTheme]                     = useState(() => localStorage.getItem('crewboard-theme') || 'dark');
  const [pendingReports, setPendingReports]     = useState(0);
  const [missingInsurance, setMissingInsurance] = useState(0);
  const [pendingAlerts, setPendingAlerts]       = useState(0);
  const [pendingAbstracts, setPendingAbstracts] = useState(0);
  const [pendingDriverCDL, setPendingDriverCDL] = useState(0);
  const [pendingHirers, setPendingHirers]       = useState(0);
  const [powPendingCount, setPowPendingCount]   = useState(0);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('crewboard-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'reports'), where('status', '==', 'pending'));
    const unsub = onSnapshot(q,
      snap => setPendingReports(snap.size),
      err  => console.warn('reports snapshot:', err.message));
    return unsub;
  }, [user]);

  useEffect(() => {
    if (!user) return;
    // Declarations & Alerts badge — declaration/system alerts only. Document-review
    // submissions (CDL/abstract/POW) have their own counters and must not inflate this.
    const DOC_REVIEW_TYPES = new Set(['license_pending_review', 'abstract_pending_review', 'pow_pending_review']);
    const q2 = query(collection(db, 'adminAlerts'), where('resolved', '==', false));
    const unsub2 = onSnapshot(q2,
      snap => setPendingAlerts(snap.docs.filter(d => !DOC_REVIEW_TYPES.has(d.data().type)).length),
      err  => console.warn('adminAlerts snapshot:', err.message));
    return unsub2;
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'users'), where('role', '==', 'driver'), where('abstractStatus', '==', 'pending'));
    const unsub = onSnapshot(q,
      snap => setPendingAbstracts(snap.size),
      err  => console.warn('abstractStatus snapshot:', err.message));
    return unsub;
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'users'), where('role', '==', 'driver'), where('licenseStatus', '==', 'pending'));
    const unsub = onSnapshot(q,
      snap => setPendingDriverCDL(snap.size),
      err  => console.warn('licenseStatus snapshot:', err.message));
    return unsub;
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'adminNotifications'),
      where('type', '==', 'insurance_confirm_missing'),
      where('read', '==', false),
    );
    const unsub = onSnapshot(q,
      snap => setMissingInsurance(snap.size),
      err  => console.warn('insurance snapshot:', err.message));
    return unsub;
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'users'),
      where('role', '==', 'hirer'),
      where('verificationStatus', '==', 'pending'),
    );
    const unsub = onSnapshot(q,
      snap => setPendingHirers(snap.size),
      err  => console.warn('hirers snapshot:', err.message));
    return unsub;
  }, [user]);

  // POW pending — fires when a driver uploads proof of work
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'users'),
      where('role',             '==', 'driver'),
      where('proofOfWorkStatus','==', 'pending'),
    );
    const unsub = onSnapshot(q,
      snap => setPowPendingCount(snap.size),
      err  => console.warn('pow pending snapshot:', err.message));
    return unsub;
  }, [user]);

  const pages = {
    dashboard:     <Dashboard onNavigate={setActive} />,
    drivers:       <DriverVerification />,
    hirers:        <HirerVerification />,
    users:         <UserManagement />,
    reports:       <ReportsPage />,
    disputes:      <DisputesPage />,
    jobs:          <JobManagement />,
    insurance:     <InsuranceConfirmations />,
    declarations:  <DeclarationsPage />,
    notifications: <Notifications />,
    subscriptions: <Subscriptions />,
    offercodes:    <OfferCodesPage />,
    privacy:       <Privacy />,
    admins:        <AdminManagement />,
  };

  const W       = collapsed ? 68 : 240;
  const isLight = theme === 'light';

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg)' }}>

      {/* ── Sidebar ── */}
      <div style={{
        width: W, minWidth: W,
        background: 'var(--bg)',
        borderRight: '1px solid var(--surface2)',
        display: 'flex', flexDirection: 'column', flexShrink: 0,
        transition: 'width 0.2s ease, min-width 0.2s ease',
        overflow: 'hidden',
      }}>

        {/* Brand + collapse toggle */}
        <div style={{
          padding: collapsed ? '18px 0' : '28px 20px 20px',
          borderBottom: '1px solid var(--surface2)',
          display: 'flex',
          alignItems: collapsed ? 'center' : 'flex-start',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: 8,
        }}>
          {!collapsed && (
            <div>
              <div style={{ fontFamily: 'Syne', fontSize: 18, fontWeight: 800, color: '#F97316', letterSpacing: 3 }}>🚛 CREW</div>
              <div style={{ fontFamily: 'Syne', fontSize: 18, fontWeight: 800, color: 'var(--text)', marginTop: -4, letterSpacing: 3 }}>BOARD</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 3, letterSpacing: 1 }}>ADMIN PANEL</div>
            </div>
          )}
          <button
            onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 8,
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: 15,
              width: 32, height: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, transition: 'all 0.15s',
            }}
          >
            {collapsed ? '›' : '‹'}
          </button>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto' }}>
          {NAV.map(item => {
            const isActive = active === item.id;
            return (
              <div
                key={item.id}
                onClick={() => setActive(item.id)}
                title={collapsed ? item.label : undefined}
                style={{
                  display: 'flex', alignItems: 'center',
                  gap: collapsed ? 0 : 12,
                  padding: collapsed ? '10px 0' : '10px 14px',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  borderRadius: 10, marginBottom: 2, cursor: 'pointer', transition: 'all 0.15s', position: 'relative',
                  background: isActive ? 'rgba(249,115,22,0.12)' : 'transparent',
                  border: isActive ? '1px solid rgba(249,115,22,0.2)' : '1px solid transparent',
                  color: isActive ? '#F97316' : 'var(--text-secondary)',
                  fontWeight: isActive ? 600 : 400, fontSize: 14,
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--surface2)'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: 18, width: 22, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
                {!collapsed && item.label}
                {!collapsed && item.id === 'drivers' && (pendingDriverCDL + pendingAbstracts + powPendingCount) > 0 && (
                  <span style={{ marginLeft: 'auto', background: '#EF4444', color: '#fff', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700, lineHeight: 1.6 }}>
                    {pendingDriverCDL + pendingAbstracts + powPendingCount}
                  </span>
                )}
                {collapsed && item.id === 'drivers' && (pendingDriverCDL + pendingAbstracts + powPendingCount) > 0 && (
                  <span style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, background: '#EF4444', borderRadius: '50%' }} />
                )}
                {!collapsed && item.id === 'reports' && pendingReports > 0 && (
                  <span style={{ marginLeft: 'auto', background: '#EF4444', color: '#fff', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700, lineHeight: 1.6 }}>
                    {pendingReports}
                  </span>
                )}
                {collapsed && item.id === 'reports' && pendingReports > 0 && (
                  <span style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, background: '#EF4444', borderRadius: '50%' }} />
                )}
                {!collapsed && item.id === 'declarations' && pendingAlerts > 0 && (
                  <span style={{ marginLeft: 'auto', background: '#EF4444', color: '#fff', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700, lineHeight: 1.6 }}>
                    {pendingAlerts}
                  </span>
                )}
                {collapsed && item.id === 'declarations' && pendingAlerts > 0 && (
                  <span style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, background: '#EF4444', borderRadius: '50%' }} />
                )}
                {!collapsed && item.id === 'insurance' && missingInsurance > 0 && (
                  <span style={{ marginLeft: 'auto', background: '#EF4444', color: '#fff', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700, lineHeight: 1.6 }}>
                    {missingInsurance}
                  </span>
                )}
                {collapsed && item.id === 'insurance' && missingInsurance > 0 && (
                  <span style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, background: '#EF4444', borderRadius: '50%' }} />
                )}
                {!collapsed && item.id === 'hirers' && pendingHirers > 0 && (
                  <span style={{ marginLeft: 'auto', background: '#EF4444', color: '#fff', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700, lineHeight: 1.6 }}>
                    {pendingHirers}
                  </span>
                )}
                {collapsed && item.id === 'hirers' && pendingHirers > 0 && (
                  <span style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, background: '#EF4444', borderRadius: '50%' }} />
                )}
              </div>
            );
          })}
        </nav>

        {/* Bottom: user + theme + sign out */}
        <div style={{
          padding: collapsed ? '12px 8px' : 16,
          borderTop: '1px solid var(--surface2)',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#F97316', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                {user.email?.charAt(0).toUpperCase()}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.email}
              </div>
            </div>
          )}

          <button
            onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
            title={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
            style={{
              width: '100%', background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 8, padding: collapsed ? '8px 0' : '8px 14px',
              color: 'var(--text-secondary)', fontSize: collapsed ? 17 : 13,
              cursor: 'pointer', transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            {isLight ? '🌙' : '☀️'}
            {!collapsed && (isLight ? ' Dark mode' : ' Light mode')}
          </button>

          <button
            onClick={() => signOut(auth)}
            title={collapsed ? 'Sign out' : undefined}
            style={{
              width: '100%', background: 'transparent', border: '1px solid var(--border)',
              borderRadius: 8, padding: collapsed ? '8px 0' : '8px 14px',
              color: 'var(--text-secondary)', fontSize: collapsed ? 17 : 13,
              cursor: 'pointer', transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#EF4444'; e.currentTarget.style.color = '#EF4444'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          >
            {collapsed ? '⏻' : 'Sign Out'}
          </button>
        </div>
      </div>

      {/* ── Main ── */}
      <main style={{ flex: 1, overflowY: 'auto', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
        {/* Page header */}
        <div style={{
          padding: '14px 32px',
          borderBottom: '1px solid var(--surface2)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>{NAV.find(n => n.id === active)?.icon}</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
              {NAV.find(n => n.id === active)?.label}
            </span>
          </div>
          <button
            onClick={() => setJobAidOpen(true)}
            title="Open Job Aid"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '6px 14px',
              color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#F97316';
              e.currentTarget.style.color = '#F97316';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            <span style={{ fontSize: 15 }}>📋</span> Job Aid
          </button>
        </div>

        <div style={{ padding: 32, maxWidth: 1200, margin: '0 auto', width: '100%' }}>
          {pages[active]}
        </div>
      </main>

      <JobAidDrawer
        open={jobAidOpen}
        onClose={() => setJobAidOpen(false)}
        pageId={active}
      />
    </div>
  );
}